import { SITE_CONFIG } from "../../app/config";
import { useHead } from "../../seo/useHead";
import { buildBreadcrumbSchema } from "../../seo/structured-data";

type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
};

const blogPosts: BlogPost[] = [
  {
    slug: "how-much-spotify-playlist-cost-on-vinyl",
    title: "How Much Would My Spotify Playlist Cost on Vinyl?",
    excerpt: "We break down the real cost of converting your favorite Spotify playlists into a vinyl collection — from budget-friendly approaches to audiophile splurges. Get an instant estimate.",
    date: "February 5, 2026",
    readTime: "10 min",
    category: "Cost Guide"
  },
  {
    slug: "spotify-playlist-to-vinyl-converter",
    title: "Spotify Playlist to Vinyl Converter: Turn Your Streams Into Records",
    excerpt: "The fastest way to find out which of your Spotify songs are available on vinyl — and where to buy them at the best price. Free, instant, no signup.",
    date: "February 3, 2026",
    readTime: "8 min",
    category: "Tool Guide"
  },
  {
    slug: "how-to-buy-spotify-songs-on-cd",
    title: "How to Buy Your Spotify Songs on CD (Step-by-Step Guide)",
    excerpt: "Everything you need to know about turning your Spotify library into a CD collection — the cheapest way to own your music forever, plus how to rip for digital.",
    date: "January 28, 2026",
    readTime: "9 min",
    category: "How-To"
  },
  {
    slug: "cost-of-owning-music-vs-streaming",
    title: "Cost of Owning Music vs Streaming: Which Is Actually Cheaper?",
    excerpt: "A no-BS financial breakdown of buying vinyl and CDs versus paying for streaming subscriptions over 5, 10, and 30 years. The math will surprise you.",
    date: "January 20, 2026",
    readTime: "11 min",
    category: "Analysis"
  },
  {
    slug: "do-you-own-your-spotify-music",
    title: "Do You Actually Own Your Spotify Music? The Truth About Streaming",
    excerpt: "What happens to your music library when you cancel Spotify — and why millions of listeners are going back to physical media they actually own.",
    date: "January 12, 2026",
    readTime: "9 min",
    category: "Opinion"
  },
  {
    slug: "spotify-songs-disappearing-from-playlist",
    title: "Why Songs Keep Disappearing From Your Spotify Playlist (And How to Fix It)",
    excerpt: "Understanding why tracks go grey on Spotify — licensing deals, artist disputes, regional restrictions — and the permanent solution that ensures you never lose your favorites.",
    date: "January 5, 2026",
    readTime: "8 min",
    category: "Explainer"
  },
  {
    slug: "convert-spotify-playlist-to-cd-vinyl",
    title: "How to Convert Your Spotify Playlist to CD or Vinyl in 2025",
    excerpt: "The complete step-by-step guide to turning your streaming playlists into a physical music collection you actually own. Free tools, where to buy, and pro tips.",
    date: "September 29, 2024",
    readTime: "11 min",
    category: "How-To"
  },
  {
    slug: "is-vinyl-worth-it-2025",
    title: "Is Vinyl Worth It in 2025? The Complete Beginner's Guide",
    excerpt: "Everything you need to know about starting a vinyl collection — costs, benefits, sound quality myths, where to buy, and whether it makes sense for you.",
    date: "September 22, 2024",
    readTime: "15 min",
    category: "Guide"
  },
  {
    slug: "vinyl-cd-vs-streaming-true-cost",
    title: "Vinyl & CD vs. Streaming: The True Cost of Music Ownership in 2025",
    excerpt: "We break down the real numbers. Is streaming actually cheaper than owning? Spoiler: the math might surprise you. Plus, why ripping your collection to Jellyfin/Navidrome is the ultimate power move.",
    date: "September 15, 2024",
    readTime: "12 min",
    category: "Analysis"
  },
  {
    slug: "where-to-buy-vinyl-cds-2025",
    title: "Where to Buy Vinyl & CDs in 2025: The Ultimate Guide",
    excerpt: "From local record shops to online marketplaces, we cover all the best places to score physical music. Featuring CD&LP, Discogs, and hidden gems you didn't know existed.",
    date: "September 8, 2024",
    readTime: "10 min",
    category: "Guide"
  },
  {
    slug: "essential-classic-rock-albums-vinyl",
    title: "15 Essential Classic Rock Albums That Sound Amazing on Vinyl",
    excerpt: "The Cure, U2, Scorpions, and more. These timeless albums were made for analog. Discover why they hit different on wax and where to grab your copies.",
    date: "September 1, 2024",
    readTime: "14 min",
    category: "Recommendations"
  }
];

export const BlogIndex = (): JSX.Element => {
  useHead({
    title: "Blog — Vinyl, CDs & Music Ownership",
    description: "Deep dives on vinyl records, CDs, music ownership, and building a physical collection. Guides, analysis, and recommendations from GoOffline.",
    path: "/blog",
    jsonLd: buildBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
    ]),
  });

  return (
  <div className="shell">
    <div className="blog-index">
      <header className="blog-index-header">
        <a href="/" className="blog-back">← Back to {SITE_CONFIG.name}</a>
        <h1 className="blog-index-title">The {SITE_CONFIG.name} Blog</h1>
        <p className="blog-index-subtitle">
          Deep dives on vinyl, CDs, music ownership, and building a collection that actually belongs to you.
        </p>
      </header>

      <div className="blog-grid">
        {blogPosts.map((post) => (
          <a key={post.slug} href={`/blog/${post.slug}`} className="blog-card">
            <div className="blog-card-category">{post.category}</div>
            <h2 className="blog-card-title">{post.title}</h2>
            <p className="blog-card-excerpt">{post.excerpt}</p>
            <div className="blog-card-meta">
              <span>{post.date}</span>
              <span className="blog-card-divider">·</span>
              <span>{post.readTime} read</span>
            </div>
          </a>
        ))}
      </div>

      <footer className="blog-index-footer">
        <div className="blog-cta">
          <h3>Turn Your Spotify Playlists Into Physical Gold</h3>
          <p>Paste any playlist, get a shopping list of vinyl & CDs. Free, instant, no signup required.</p>
          <a href="/" className="blog-cta-button">Try {SITE_CONFIG.name} →</a>
        </div>
      </footer>
    </div>
  </div>
  );
};

